import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Heart,
  Sparkles,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  LegalTopicIntroHero,
  MediaCarousel,
  PodcastCard,
  SectionHead,
  VideoCard,
  foregroundOnSolidAccent,
} from "../shared/categoryDetailShared";

const ListBlock = ({ items, color, icon: Icon = CheckCircle2 }) => (
  <ul className="space-y-2.5">
    {items.map((line) => (
      <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-gray-800 sm:text-[0.9375rem]">
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: `${color}14`, color }}
        >
          <Icon size={14} strokeWidth={2.25} />
        </span>
        <span className="min-w-0 pt-0.5">{line}</span>
      </li>
    ))}
  </ul>
);

/** Overview — Marriage & family law in Ghana (verbatim source copy). */
export const MarriageFamilyOverviewTab = ({ data, color }) => (
  <div className="space-y-10">
    <LegalTopicIntroHero
      color={color}
      categoryEyebrow="LEGAL · MARRIAGE &amp; FAMILY LAW (GHANA)"
      funnelTagline={data.funnelTagline}
      FunnelIcon={Heart}
      gallerySrc={data.galleryImages[0]}
      galleryAlt="Couple reviewing documents — marriage and family legal context"
      galleryImgClassName="object-[center_28%] sm:object-[center_22%]"
      photoEditorialFrame
    >
      <h2 className="max-w-2xl text-[1.55rem] font-black leading-[1.14] tracking-[-0.02em] text-slate-950 sm:text-[1.9rem] lg:text-[2.05rem]">
        {data.introduction.headline}
      </h2>
      <p className="mt-5 max-w-prose text-base leading-relaxed text-slate-600 sm:text-[1.05rem] sm:leading-relaxed">{data.introduction.lead}</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {data.focusAreas.map((tag) => (
          <span
            key={tag}
            className="rounded-xl border border-slate-200/95 bg-white/95 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 shadow-sm ring-1 ring-slate-200/55"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200/95 bg-white/90 p-5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.85)] backdrop-blur-sm sm:p-7">
        <ListBlock items={data.introduction.bullets} color={color} />
      </div>

      <div className="mt-6 rounded-2xl border border-slate-900/[0.08] bg-slate-950/[0.04] px-5 py-5 sm:px-7 sm:py-6">
        <p className="text-sm font-bold text-slate-900">{data.introduction.whetherYouAreLead}</p>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {data.introduction.scenarios.map((s) => (
            <li key={s} className="flex items-start gap-2 text-sm font-semibold text-slate-700">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" aria-hidden />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </LegalTopicIntroHero>

    <section>
      <SectionHead
        eyebrow="Very important"
        title={data.marriageTypesSectionTitle}
        subtitle={data.marriageTypesIntro}
        color={color}
      />
      <div className="grid gap-4 lg:grid-cols-3">
        {data.marriageTypes.map((mt) => (
          <motion.article
            key={mt.number}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            className="flex flex-col rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm"
          >
            <span className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color }}>
              {mt.number}. {mt.title}
            </span>
            <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-700">
              {mt.bullets.map((b) => (
                <li key={b}>• {b}</li>
              ))}
            </ul>
          </motion.article>
        ))}
      </div>

      <div
        className="mt-6 overflow-hidden rounded-xl border-2 p-6 sm:p-7"
        style={{ borderColor: `${color}55`, background: `${color}0a` }}
      >
        <p className="text-sm font-black text-slate-950">{data.marriageTypesKeyInsight.label}</p>
        <p className="mt-2 text-sm font-semibold text-slate-700">{data.marriageTypesKeyInsight.lead}</p>
        <ul className="mt-3 space-y-1.5 text-sm font-semibold text-slate-800">
          {data.marriageTypesKeyInsight.bullets.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <p className="mt-4 text-sm font-bold text-slate-900">{data.marriageTypesKeyInsight.footer}</p>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead
          eyebrow="Civil marriage"
          title={data.marriageRegistration.title}
          subtitle={data.marriageRegistration.civilHeading}
          color={color}
        />
        <p className="mb-3 text-xs font-black uppercase tracking-wider text-slate-500">{data.marriageRegistration.stepsIntro}</p>
        <ol className="list-decimal space-y-2 pl-5 text-sm font-semibold text-slate-800">
          {data.marriageRegistration.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <p className="mt-6 text-xs font-black uppercase tracking-wider text-slate-500">
          {data.marriageRegistration.requiredDocumentsHeading}
        </p>
        <ul className="mt-2 space-y-1.5 text-sm font-semibold text-slate-700">
          {data.marriageRegistration.requiredDocuments.map((d) => (
            <li key={d}>• {d}</li>
          ))}
        </ul>
      </div>
      <div className="space-y-4">
        <div className="rounded-xl border border-slate-200/90 bg-slate-50/80 p-6">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.marriageRegistration.whyHeading}
          </p>
          <p className="mt-2 text-sm font-bold text-slate-900">{data.marriageRegistration.whyLead}</p>
          <p className="mt-1 text-sm font-semibold text-slate-700">{data.marriageRegistration.whyAlso}</p>
          <p className="mt-4 text-sm font-bold text-slate-900">{data.marriageRegistration.whyRequiredIntro}</p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
            {data.marriageRegistration.whyRequired.map((r) => (
              <li key={r}>o {r}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-6">
          <p className="flex items-center gap-2 text-sm font-black text-amber-950">
            <AlertTriangle size={16} className="shrink-0 text-amber-600" />
            {data.marriageRegistration.mistakesHeading}
          </p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-amber-950/90">
            {data.marriageRegistration.mistakes.map((m) => (
              <li key={m}>• {m}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="rounded-xl border border-slate-900/[0.08] bg-slate-950 p-6 text-white sm:p-10">
      <div className="mb-6 h-[3px] w-14 rounded-full" style={{ background: color }} aria-hidden />
      <h3 className="text-xl font-black tracking-tight sm:text-2xl">{data.divorce.title}</h3>
      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.24em]" style={{ color }}>
            {data.divorce.keyLawHeading}
          </p>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-white/85">{data.divorce.keyLaw}</p>
          <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.04] p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/55">{data.divorce.importantRuleHeading}</p>
            <p className="mt-3 text-sm font-black text-white">{data.divorce.importantRuleLead}</p>
            <p className="mt-3 text-sm font-semibold text-white/75">{data.divorce.importantRuleBody}</p>
            <p className="mt-2 text-base font-black text-white [text-wrap:balance]">{data.divorce.importantRuleProof}</p>
          </div>
          <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em] text-white/55">{data.divorce.groundsHeading}</p>
          <ul className="mt-2 space-y-1.5 text-sm font-semibold text-white/80">
            {data.divorce.grounds.map((g) => (
              <li key={g}>• {g}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/55">{data.divorce.processHeading}</p>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm font-semibold text-white/85">
            {data.divorce.processSteps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          <p className="mt-6 text-sm font-bold text-white">{data.divorce.courtsLead}</p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-white/80">
            {data.divorce.courts.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
          <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em]" style={{ color }}>
            {data.divorce.courtDecidesHeading}
          </p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-white/85">
            {data.divorce.courtDecides.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl border border-amber-500/25 bg-amber-500/[0.08] p-4">
            <p className="text-xs font-black text-amber-200">{data.divorce.notesHeading}</p>
            <ul className="mt-2 space-y-2 text-sm font-semibold text-amber-50/95">
              {data.divorce.notes.map((n) => (
                <li key={n}>• {n}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="grid gap-6 xl:grid-cols-12">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8 xl:col-span-7">
        <SectionHead eyebrow="Sensitive issue" title={data.childCustody.title} subtitle={data.childCustody.sensitivity} color={color} />
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.childCustody.principleHeading}</p>
        <p className="mt-2 text-sm font-black text-slate-950">{data.childCustody.principleLead}</p>
        <p className="mt-3 border-l-4 pl-4 text-lg font-black italic text-slate-900" style={{ borderColor: color }}>
          {data.childCustody.principleQuote}
        </p>
        <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.childCustody.factorsHeading}</p>
        <ul className="mt-2 space-y-1.5 text-sm font-semibold text-slate-800">
          {data.childCustody.factors.map((f) => (
            <li key={f}>• {f}</li>
          ))}
        </ul>
        <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em]" style={{ color }}>
          {data.childCustody.typesHeading}
        </p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
          {data.childCustody.types.map((t) => (
            <li key={t}>• {t}</li>
          ))}
        </ul>
      </div>
      <div className="space-y-4 xl:col-span-5">
        <div className="rounded-xl border border-slate-200/90 bg-slate-50/90 p-6">
          <p className="text-xs font-black uppercase tracking-wider text-slate-600">{data.childCustody.visitationHeading}</p>
          <p className="mt-2 text-sm font-bold text-slate-900">{data.childCustody.visitationLead}</p>
          <p className="mt-2 text-sm font-semibold text-slate-800">• {data.childCustody.visitationBullet}</p>
        </div>
        <div className="rounded-xl border border-rose-100 bg-rose-50/70 p-6">
          <p className="text-sm font-black text-rose-950">{data.childCustody.issuesHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-rose-900/90">
            {data.childCustody.issues.map((i) => (
              <li key={i}>• {i}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <h3 className="text-xl font-black text-slate-950">{data.prenuptial.title}</h3>
        <p className="mt-6 text-xs font-black uppercase tracking-wider text-slate-500">{data.prenuptial.whatHeading}</p>
        <p className="mt-2 text-sm font-semibold text-slate-700">{data.prenuptial.whatLead}</p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
          {data.prenuptial.whatBullets.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <p className="mt-6 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.prenuptial.whyHeading}
        </p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
          {data.prenuptial.whyBullets.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
      </div>
      <div className="flex flex-col justify-center rounded-xl border border-dashed border-slate-300 bg-gradient-to-br from-white to-slate-50 p-6 sm:p-8">
        <p className="text-sm font-black text-slate-950">{data.prenuptial.misconceptionHeading}</p>
        <p className="mt-3 text-sm font-semibold text-slate-700">{data.prenuptial.misconceptionLead}</p>
        <p className="mt-2 text-base font-black text-slate-900">{data.prenuptial.misconceptionQuoted}</p>
        <p className="mt-6 text-sm font-black text-slate-900">{data.prenuptial.misconceptionTruthLead}</p>
        <p className="mt-2 text-sm font-bold leading-relaxed text-slate-800">{data.prenuptial.misconceptionTruth}</p>
      </div>
    </section>

    <section>
      <SectionHead
        eyebrow="When marriage ends"
        title={data.propertyDivision.title}
        subtitle={data.propertyDivision.intro}
        color={color}
      />
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 md:col-span-2">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.propertyDivision.courtHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.propertyDivision.courtBullets.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl border border-slate-100 bg-slate-50 p-5">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {data.propertyDivision.insightHeading}
            </p>
            <p className="mt-2 text-sm font-semibold text-slate-700">{data.propertyDivision.insightBody}</p>
            <p className="mt-3 text-lg font-black text-slate-950">{data.propertyDivision.insightEmphasis}</p>
          </div>
        </div>
        <div className="rounded-xl border border-amber-200/70 bg-amber-50/60 p-6">
          <p className="text-sm font-black text-amber-950">{data.propertyDivision.problemsHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-amber-950/95">
            {data.propertyDivision.problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-10">
      <SectionHead eyebrow="On the ground" title={data.commonProblemsGhana.title} subtitle={data.commonProblemsGhana.intro} color={color} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.commonProblemsGhana.issues.map((issue) => (
          <div
            key={issue}
            className="rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3 text-sm font-bold text-slate-800"
          >
            • {issue}
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Action list" title={data.practicalTips.title} color={color} />
      <div className="grid gap-4 sm:grid-cols-2">
        {[
          data.practicalTips.beforeMarriage,
          data.practicalTips.duringMarriage,
          data.practicalTips.duringDivorce,
          data.practicalTips.forChildren,
        ].map((block) => (
          <motion.div
            key={block.heading}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm"
          >
            <p className="text-sm font-black text-slate-950">{block.heading}</p>
            <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-700">
              {block.items.map((it) => (
                <li key={it}>{it}</li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </section>

    <section>
      <div className="overflow-hidden rounded-xl border border-red-200/80 bg-gradient-to-br from-red-50/90 to-white">
        <div className="flex items-start gap-3 border-b border-red-100 px-6 py-4 sm:px-8">
          <AlertTriangle className="mt-0.5 shrink-0 text-red-500" size={20} />
          <h3 className="text-lg font-black text-gray-950 sm:text-xl">{data.redFlags.title}</h3>
        </div>
        <div className="space-y-2 p-6 sm:p-8 sm:pt-4">
          {data.redFlags.items.map((flag) => (
            <div
              key={flag}
              className="rounded-lg border border-red-100/90 bg-white/90 px-4 py-3 text-sm font-semibold text-red-950"
            >
              {flag}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Funnel" title={data.funnelApplication.title} color={color} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          data.funnelApplication.hook,
          data.funnelApplication.education,
          data.funnelApplication.authority,
          data.funnelApplication.conversion,
        ].map((step) => (
          <div
            key={step.label}
            className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm"
          >
            <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color }}>
              {step.label}
            </p>
            <p className="mt-3 text-sm font-bold leading-relaxed text-slate-900">{step.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-slate-950 p-8 text-white sm:p-10">
      <div className="absolute inset-0 opacity-30 blur-3xl" style={{ background: `radial-gradient(circle at 30% 20%, ${color}, transparent 55%)` }} aria-hidden />
      <div className="relative">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.finalTakeaway.title}
        </p>
        <ul className="mt-6 space-y-4">
          {data.finalTakeaway.bullets.map((b) => (
            <li key={b} className="flex gap-3 text-base font-bold leading-relaxed text-white/95 sm:text-lg">
              <CheckCircle2 size={22} className="shrink-0 opacity-90" style={{ color }} aria-hidden />
              {b}
            </li>
          ))}
        </ul>
        <div className="mt-10 border-t border-white/10 pt-8">
          <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.bottomLine.title}
          </p>
          <p className="mt-3 text-lg font-black text-white sm:text-xl">{data.bottomLine.lead}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
            <p className="rounded-lg border border-white/15 bg-white/[0.06] p-4 text-center text-sm font-bold text-white sm:text-base">
              {data.bottomLine.smooth}
            </p>
            <span className="hidden text-center text-sm font-black text-white/50 sm:block">{data.bottomLine.and}</span>
            <p className="rounded-lg border border-red-500/30 bg-red-950/35 p-4 text-center text-sm font-bold text-red-100 sm:text-base">
              {data.bottomLine.stressful}
            </p>
          </div>
          <p className="mt-6 flex flex-wrap items-center gap-2 text-sm font-semibold text-white/70">
            <ArrowRight size={16} style={{ color }} />
            {data.bottomLine.closing}
          </p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Frequently Asked" title="Common Questions" color={color} />
      <div className="space-y-2">
        {data.faqs.map((faq) => (
          <FAQItem key={faq.q} q={faq.q} a={faq.a} color={color} />
        ))}
      </div>
    </section>
  </div>
);

export const MarriageFamilyArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-10">
    <section>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <SectionHead
          eyebrow="Content Library"
          title="Articles &amp; Guides"
          subtitle={data.funnelTagline}
          color={color}
        />
        <span className="mb-8 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {data.articles.length} articles
        </span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section
      className="overflow-hidden rounded-xl p-8 sm:p-10"
      style={{
        background: `linear-gradient(128deg, ${color}12, transparent)`,
        border: `1px solid ${color}35`,
      }}
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            Lead CTAs
          </p>
          <h3 className="text-xl font-black text-slate-950 sm:text-2xl">Talk to a family lawyer</h3>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-600">
            <li>• Get legal advice on divorce</li>
            <li>• Book a consultation</li>
            <li>• Protect your assets</li>
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl px-7 py-4 text-sm font-black shadow-md transition hover:scale-[1.02]"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register Free
          <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  </div>
);

export const MarriageFamilyMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHead
          eyebrow="Watch &amp; listen"
          title="Videos &amp; podcasts"
          subtitle={data.funnelTagline}
          color={color}
        />
        <span className="mt-1 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {total} featured
        </span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="mfl-media-videos-heading" className="min-w-0">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Marriage and family law videos"
            items={videos}
            header={<h3 id="mfl-media-videos-heading" className="text-lg font-black text-slate-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="mfl-media-podcasts-heading" className="min-w-0">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Marriage and family law podcast episodes"
            items={podcasts}
            header={<h3 id="mfl-media-podcasts-heading" className="text-lg font-black text-slate-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
          <p className="mt-4 px-2 text-center text-xs leading-relaxed text-gray-400 sm:text-left sm:text-sm md:mt-6">
            Listen links appear here when Spotify, Apple Podcasts, or other feeds are connected from admin or your CMS.
          </p>
        </section>
      ) : null}

      <div className="overflow-hidden rounded-xl bg-slate-950 text-white shadow-lg sm:rounded-[1.5rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-6 sm:p-8 md:p-10">
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles size={11} style={{ color }} />
              For partners
            </div>
            <h3 className="text-xl font-black leading-snug sm:text-2xl">{data.funnelApplication.conversion.text}</h3>
            <p className="mt-3 text-sm font-bold leading-relaxed text-white/85">Talk to a family lawyer</p>
            <Link
              to="/partners/onboard"
              className="mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black"
              style={{ background: color, color: foregroundOnSolidAccent(color) }}
            >
              Become a partner
              <ArrowRight size={14} />
            </Link>
          </div>
          <div className="relative min-h-[12rem] sm:min-h-[14rem] lg:min-h-0">
            <img src={data.galleryImages[1]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-950/85" />
          </div>
        </div>
      </div>
    </div>
  );
};
